/**
 * Video Firestore Service
 * Handles CRUD operations for videos collection
 */

import { database, storage } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  increment,
  CollectionReference,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import { ServiceResult, ListResult, VideoFormData, VideoQueryOptions, VideoCategory } from '../types/admin';

export type VideoDoc = {
  id: string;
  title: string;
  description: string;
  category: VideoCategory;
  youtubeUrl: string;
  thumbnailUrl: string;
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  isDraft: boolean;
  isPublished: boolean;
  createdAt: any;
  updatedAt: any;
  publishedAt?: any;
  createdBy?: string;
  lastModifiedBy?: string;
  viewCount?: number;
  playCount?: number;
  downloadCount?: number;
};

class VideoService {
  private db: typeof database;
  private collectionName: string;
  private collection: CollectionReference<DocumentData>;

  constructor() {
    this.db = database;
    this.collectionName = 'videos';
    this.collection = collection(this.db, this.collectionName);
  }

  validateVideoData(
    videoData: Partial<VideoFormData>,
    isUpdate = false
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    const requiredFields: (keyof VideoFormData)[] = ['title', 'description', 'category'];
    requiredFields.forEach((field) => {
      const value = videoData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`${field} is required`);
      }
    });

    if (videoData.title && videoData.title.length < 3) {
      errors.push('Title must be at least 3 characters long');
    }
    if (videoData.title && videoData.title.length > 200) {
      errors.push('Title must be less than 200 characters');
    }
    if (videoData.description && videoData.description.length < 10) {
      errors.push('Description must be at least 10 characters long');
    }
    if (videoData.description && videoData.description.length > 2000) {
      errors.push('Description must be less than 2000 characters');
    }

    if (videoData.youtubeUrl && !this.isValidYouTubeUrl(videoData.youtubeUrl)) {
      errors.push('YouTube URL is invalid');
    }

    if (!isUpdate) {
      if (!videoData.youtubeUrl || videoData.youtubeUrl.trim() === '') {
        errors.push('YouTube URL is required');
      }
      if (!videoData.thumbnailUrl && !videoData.thumbnailFile) {
        errors.push('Thumbnail is required');
      }
    }

    return { isValid: errors.length === 0, errors };
  }

  private isValidYouTubeUrl(url: string): boolean {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');
      if (host === 'youtu.be') return Boolean(u.pathname.slice(1));
      if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (u.pathname === '/watch') return Boolean(u.searchParams.get('v'));
        if (u.pathname.startsWith('/shorts/')) return Boolean(u.pathname.split('/')[2]);
        if (u.pathname.startsWith('/embed/')) return Boolean(u.pathname.split('/')[2]);
      }
      return false;
    } catch {
      return false;
    }
  }

  normalizeYouTubeUrl(url: string): string {
    if (!url) return '';
    try {
      const u = new URL(url.trim());
      const host = u.hostname.replace(/^www\./, '');
      let id = '';
      if (host === 'youtu.be') id = u.pathname.slice(1);
      if ((host === 'youtube.com' || host === 'm.youtube.com') && u.pathname === '/watch') {
        id = u.searchParams.get('v') || '';
      }
      if ((host === 'youtube.com' || host === 'm.youtube.com') && u.pathname.startsWith('/shorts/')) {
        id = u.pathname.split('/')[2] || '';
      }
      if ((host === 'youtube.com' || host === 'm.youtube.com') && u.pathname.startsWith('/embed/')) {
        id = u.pathname.split('/')[2] || '';
      }
      if (!id) return url.trim();
      return `https://www.youtube.com/watch?v=${id}`;
    } catch {
      return url.trim();
    }
  }

  getYouTubeEmbedUrl(url: string): string {
    const normalized = this.normalizeYouTubeUrl(url);
    try {
      const u = new URL(normalized);
      const v = u.searchParams.get('v');
      if (!v) return normalized;
      return `https://www.youtube.com/embed/${v}`;
    } catch {
      return normalized;
    }
  }

  async createDraft(): Promise<ServiceResult<{ docId: string }>> {
    try {
      const draftData = {
        isDraft: true,
        isPublished: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft',
      };
      const docRef = await addDoc(this.collection, draftData);
      return { success: true, id: docRef.id, data: { docId: docRef.id } };
    } catch (error: any) {
      console.error('Create video draft error:', error);
      toast.error(`Failed to create draft: ${error.message}`);
      throw error;
    }
  }

  async uploadThumbnail(docId: string, file: File): Promise<string> {
    try {
      const filePath = `videos/${docId}/thumbnail.png`;
      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    } catch (error: any) {
      console.error('Upload thumbnail error:', error);
      toast.error(`Failed to upload thumbnail: ${error.message}`);
      throw error;
    }
  }

  async updateDraft(docId: string, updates: Record<string, any>): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
      return { success: true, id: docId };
    } catch (error: any) {
      console.error('Update video draft error:', error);
      toast.error(`Failed to update draft: ${error.message}`);
      throw error;
    }
  }

  async publishDraft(docId: string, videoData: Partial<VideoDoc>): Promise<ServiceResult<VideoDoc>> {
    try {
      const formData: Partial<VideoFormData> = {
        title: videoData.title || '',
        description: videoData.description || '',
        category: (videoData.category as VideoCategory) || 'longvideo',
        youtubeUrl: videoData.youtubeUrl || '',
        thumbnailUrl: videoData.thumbnailUrl || '',
        status: 'published',
      };

      const validation = this.validateVideoData(formData, false);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const docRef = doc(this.db, this.collectionName, docId);
      const publishedData = {
        ...videoData,
        title: (videoData.title || '').trim(),
        description: (videoData.description || '').trim(),
        youtubeUrl: this.normalizeYouTubeUrl(videoData.youtubeUrl || ''),
        updatedBy: 'admin',
        isDraft: false,
        isPublished: true,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'published',
      };

      await updateDoc(docRef, publishedData);

      toast.success('Video published successfully');
      return { success: true, id: docId, data: { ...(videoData as any), id: docId, ...publishedData } as VideoDoc };
    } catch (error: any) {
      console.error('Publish video draft error:', error);
      toast.error(`Failed to publish video: ${error.message}`);
      throw error;
    }
  }

  async deleteDraftDocOnly(docId: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await deleteDoc(docRef);
      return { success: true, id: docId };
    } catch (error: any) {
      console.error('Delete video draft error:', error);
      throw error;
    }
  }

  async deleteThumbnailFromStorage(thumbnailUrl: string): Promise<ServiceResult<void>> {
    try {
      if (!thumbnailUrl) {
        return { success: true, id: '' };
      }

      // Only delete if this is a Firebase Storage URL.
      // If it's external, skip gracefully.
      if (!thumbnailUrl.startsWith('https://firebasestorage.googleapis.com') && !thumbnailUrl.startsWith('gs://')) {
        return { success: true, id: thumbnailUrl };
      }

      // Firebase `ref(storage, url)` supports gs:// and https download URLs.
      const storageRef = ref(storage, thumbnailUrl);
      await deleteObject(storageRef);
      return { success: true, id: storageRef.fullPath };
    } catch (error: any) {
      console.error('Delete thumbnail from storage error:', error);
      throw error;
    }
  }

  async createVideo(videoData: Partial<VideoFormData>): Promise<ServiceResult<VideoDoc>> {
    try {
      const validation = this.validateVideoData(videoData, false);
      if (!validation.isValid) throw new Error(validation.errors.join(', '));

      const docData = {
        title: videoData.title?.trim() || '',
        description: videoData.description?.trim() || '',
        category: videoData.category,
        youtubeUrl: this.normalizeYouTubeUrl(videoData.youtubeUrl || ''),
        thumbnailUrl: videoData.thumbnailUrl || '',
        status: videoData.status || 'draft',
        featured: Boolean(videoData.featured),
        isDraft: videoData.status !== 'published',
        isPublished: videoData.status === 'published',
        viewCount: 0,
        playCount: 0,
        downloadCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: videoData.createdBy || 'admin',
        lastModifiedBy: videoData.lastModifiedBy || 'admin',
      };

      const docRef = await addDoc(this.collection, docData);
      toast.success('Video created successfully');
      return { success: true, id: docRef.id, data: { ...docData, id: docRef.id } as VideoDoc };
    } catch (error: any) {
      console.error('Create video error:', error);
      toast.error(`Failed to create video: ${error.message}`);
      throw error;
    }
  }

  async getVideo(id: string): Promise<ServiceResult<VideoDoc>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error('Video not found');
      return { success: true, id: docSnap.id, data: { ...docSnap.data(), id: docSnap.id } as VideoDoc };
    } catch (error: any) {
      console.error('Get video error:', error);
      toast.error(`Failed to get video: ${error.message}`);
      throw error;
    }
  }

  async updateVideo(id: string, updateData: Partial<VideoFormData>): Promise<ServiceResult<VideoDoc>> {
    try {
      const validation = this.validateVideoData(updateData, true);
      if (!validation.isValid) throw new Error(validation.errors.join(', '));

      const docRef = doc(this.db, this.collectionName, id);
      const docData: any = {
        ...updateData,
        updatedAt: serverTimestamp(),
        lastModifiedBy: (updateData as any).lastModifiedBy || 'admin',
      };

      if (typeof docData.youtubeUrl === 'string') {
        docData.youtubeUrl = this.normalizeYouTubeUrl(docData.youtubeUrl);
      }

      Object.keys(docData).forEach((key) => {
        if (docData[key] === undefined) delete docData[key];
      });

      await updateDoc(docRef, docData);
      toast.success('Video updated successfully');
      return { success: true, id, data: { ...docData, id } as VideoDoc };
    } catch (error: any) {
      console.error('Update video error:', error);
      toast.error(`Failed to update video: ${error.message}`);
      throw error;
    }
  }

  async deleteVideo(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await deleteDoc(docRef);
      toast.success('Video deleted successfully');
      return { success: true, id };
    } catch (error: any) {
      console.error('Delete video error:', error);
      toast.error(`Failed to delete video: ${error.message}`);
      throw error;
    }
  }

  async getVideos(options: VideoQueryOptions = {}): Promise<ListResult<VideoDoc>> {
    try {
      const {
        limit: limitCount = 20,
        orderBy: orderByField = 'createdAt',
        orderDirection = 'desc',
        status = null,
        category = null,
        featured = null,
        search = null,
        isPublished = true,
        startAfterDoc = null,
      } = options;

      let q: any = query(this.collection);

      if (status) q = query(q, where('status', '==', status));
      if (category) q = query(q, where('category', '==', category));
      if (featured !== null) q = query(q, where('featured', '==', featured));
      if (typeof isPublished === 'boolean') q = query(q, where('isPublished', '==', isPublished));

      q = query(q, orderBy(orderByField, orderDirection));
      if (startAfterDoc) q = query(q, startAfter(startAfterDoc));

      const querySnapshot = await getDocs(q);
      const videos: VideoDoc[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

      querySnapshot.forEach((d) => {
        videos.push({ id: d.id, ...(d.data() as any) } as VideoDoc);
        lastDoc = d as unknown as QueryDocumentSnapshot<DocumentData>;
      });

      let filtered = videos;
      if (search) {
        const s = search.toLowerCase();
        filtered = videos.filter((v) => v.title?.toLowerCase().includes(s) || v.description?.toLowerCase().includes(s));
      }

      return {
        success: true,
        data: filtered,
        error: null,
        pagination: {
          hasMore: videos.length === limitCount,
          lastDoc,
          total: filtered.length,
        },
      };
    } catch (error: any) {
      console.error('Get videos error:', error);
      toast.error(`Failed to get videos: ${error.message}`);
      throw error;
    }
  }

  async incrementViewCount(id: string): Promise<ServiceResult<void>> {
    const docRef = doc(this.db, this.collectionName, id);
    await updateDoc(docRef, { viewCount: increment(1), updatedAt: serverTimestamp() });
    return { success: true, id };
  }

  async incrementPlayCount(id: string): Promise<ServiceResult<void>> {
    const docRef = doc(this.db, this.collectionName, id);
    await updateDoc(docRef, { playCount: increment(1), updatedAt: serverTimestamp() });
    return { success: true, id };
  }

  async deleteDraftVideos(): Promise<ServiceResult<void>> {
    try {
      const q = query(this.collection, where('isDraft', '==', true));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(this.db);
      querySnapshot.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      return { success: true, id: 'draftVideos' };
    } catch (error: any) {
      console.error('Delete draft videos error:', error);
      toast.error(`Failed to delete draft videos: ${error.message}`);
      throw error;
    }
  }
}

const videoService = new VideoService();
export default videoService;

